import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/**
 * White card header with page title on the left and breadcrumb trail on the right
 .*
 * @param {{
 *   title: string,
 *   subtitle?: string,
 *   crumbs: Array<{ label: string, path?: string }>,
 * }} props
 *
 * The last item in `crumbs` is treated as the current page (non-clickable, bold)
 .* Earlier items with a `path` are clickable links
 .*
 * @example
 * <BreadcrumbHeader
 *   title="INC10042"
 *   subtitle="Sterling Capital Partners"
 *   crumbs={[{ label: 'Orders' }, { label: 'Part Orders', path: '/orders/parts' }, { label: 'INC10042' }]}
 * />
 */
import ContentCard from "./ContentCard";
export default function BreadcrumbHeader({ title, subtitle, crumbs = [] }) {
  const navigate = useNavigate();

  return (
    <ContentCard
      sx={{ px: 3,
        py: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
      
      {/* Page title + optional subtitle */}
      <Box>
        <Typography
          sx={{ fontWeight: 700, fontSize: '1.125rem', color: 'var(--grey-900)', lineHeight: 1.3 }}>
          
          {title}
        </Typography>
        {subtitle &&
        <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: '2px' }}>
            {subtitle}
          </Typography>
        }
      </Box>

      {/* Breadcrumb trail */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {/* Home icon */}
        <HomeIcon
          onClick={() => navigate('/')}
          sx={{ fontSize: 16,
            color: '#009688',
            cursor: 'pointer',
            flexShrink: 0,
            '&:hover': { opacity: 0.75 }
          }} />

        {crumbs.map((crumb, idx) => {
          const isLast = idx === crumbs.length - 1;
          return (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <ChevronRightIcon sx={{ fontSize: 14, color: 'var(--grey-400)', flexShrink: 0 }} />
              <Typography
                onClick={crumb.path ? () => navigate(crumb.path) : undefined}
                sx={{ fontSize: '0.8125rem',
                  fontWeight: isLast ? 600 : 400,
                  color: isLast ? 'var(--grey-800)' : 'var(--grey-500)',
                  cursor: crumb.path ? 'pointer' : 'default',
                  whiteSpace: 'nowrap',
                  ...(crumb.path && {
                    '&:hover': { textDecoration: 'underline', color: 'var(--grey-700)' }
                  })
                }}>
                
                {crumb.label}
              </Typography>
            </Box>);

        })}
      </Box>
    </ContentCard>);

}